/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useHistory, useLocation, useParams } from 'react-router';

import { Link } from 'react-router-dom';




/**
 * Used to create a style hook to utilise JSS
 */
import { Theme, makeStyles } from '@material-ui/core/styles';

/**
 * Used to format text.
 */
import Typography from '@material-ui/core/Typography';

/**
 * Used to style buttons.
 */
import Button from '@material-ui/core/Button';

import Tooltip from '@material-ui/core/Tooltip';

import { Column } from 'react-table';

import Chip from '@material-ui/core/Chip';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get the BCT Website URL.
 */

import { RequestState } from '@ngt/request-utilities';

import * as Dtos from '../api/Neon.Dtos';

import { DateTime } from 'luxon';

import { ALL_COUNTRIES_CODE, ALL_INSTITUTIONS_CODE, ALL_TREATMENTS_CODE, IInstitution, IPatient, InstitutionSelector, InstitutionsContext, PatientSummaryTable, usePatients } from '@ngt/online-patient-management-ui';

import { Paper } from '@material-ui/core';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

import PatientBreadcrumbs from '../components/Breadcrumbs/PatientBreadcrumbs';

import { default as PatientSummaryStatusesTable } from '../components/Table/PatientSummaryTable';
import PageLayout from 'src/components/Layout/PageLayout';
import client from '../api/client';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IRegistrationParams {
    countryCode?: string;
    institutionCode?: string;
    treatmentId?: string;
}

interface IRegistrationProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    patientSummaries: {
        marginTop: theme.spacing(3)
    },
    addPatient: {
        width: '100%',
        marginRight: theme.spacing(1)
    },
    enrolmentStatus: {
        width: '100%'
    },
    chip: {
        backgroundColor: '#987ba7',
        color: theme.palette.common.white,
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const TARGET_ACCRUAL = 164;

const administerOpmsPermissions = [Dtos.Permission.OpmsAdminister];

const medicalReviewPermissions = [Dtos.Permission.MedicalReviewPerform];

const patientUpdatePermissions = [Dtos.Permission.OpmsPatientUpdate];



/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

const usePreregisteredColumns = (institutions: IInstitution[] | null) => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Patient>> = [
            {
                Header: 'Study Number',
                accessor: 'studyNumber',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Initials',
                accessor: 'initials',
                minWidth: 110,
                maxWidth: 110
            },
            {
                id: 'siteCode',
                Header: 'Site Code',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.code;
                },
                Cell: (props: any) => {
                    return (
                        <Chip
                            label={props.cell.value}
                            className={classes.chip}
                            size="small"
                        />
                    );
                },
                minWidth: 125,
                maxWidth: 125
            },
            {
                id: 'institution',
                Header: 'Institution',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.name;
                },
                minWidth: 200,
                width: 'auto'
            },
            {
                id: 'enteredDate',
                Header: 'Date Commenced Trial',
                accessor: patient => patient.enteredDate,
                Cell: (props: any) => DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy'),
                minWidth: 210,
                maxWidth: 210
            }
        ]

        return cols;
    }, [institutions]);

    return columns;
};

const useRegisteredColumns = (institutions: IInstitution[] | null) => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Patient>> = [
            {
                Header: 'Study Number',
                accessor: 'studyNumber',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Initials',
                accessor: 'initials',
                minWidth: 110,
                maxWidth: 110
            },
            {
                id: 'siteCode',
                Header: 'Site Code',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.code;
                },
                Cell: (props: any) => {
                    return (
                        <Chip
                            label={props.cell.value}
                            className={classes.chip}
                            size="small"
                        />
                    );
                },
                minWidth: 125,
                maxWidth: 125
            },
            {
                id: 'institution',
                Header: 'Institution',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.name;
                },
                minWidth: 200,
                width: 'auto'
            },
            {
                id: 'registrationDate',
                Header: 'Registration Date',
                accessor: patient => patient.registrationDate,
                Cell: (props: any) => props.cell.value ? DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy') : null,
                minWidth: 175,
                maxWidth: 175
            },
            {
                Header: 'Age',
                accessor: 'age',
                minWidth: 90,
                maxWidth: 90
            }
        ]

        return cols;
    }, [institutions]);

    return columns;
};

const useRandomisationColumns = (institutions: IInstitution[] | null) => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Patient>> = [
            {
                Header: 'Study Number',
                accessor: 'studyNumber',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Initials',
                accessor: 'initials',
                minWidth: 110,
                maxWidth: 110
            },
            {
                id: 'siteCode',
                Header: 'Site Code',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.code;
                },
                Cell: (props: any) => {
                    return (
                        <Chip
                            label={props.cell.value}
                            className={classes.chip}
                            size="small"
                        />
                    );
                },
                minWidth: 125,
                maxWidth: 125
            },
            {
                id: 'institution',
                Header: 'Institution',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.name;
                },
                minWidth: 200,
                width: 'auto'
            },
            {
                Header: 'Date',
                id: 'randomisationDate',
                accessor: patient => patient.treatmentId === 3 ? patient.registrationDate : patient.randomisationDate,
                Cell: (props: any) => props.cell.value ? DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy') : null,
                minWidth: 195,
                maxWidth: 195
            },
            {
                Header: 'Treatment Arm',
                id: 'treatmentArm',
                accessor: patient => patient.treatmentId === 1 ? 'Arm A' : patient.treatmentId === 2 ? 'Arm B' : patient.treatmentId === 3 ? 'Arm C' : '',
                minWidth: 195,
                maxWidth: 195
            },
            {
                Header: 'Age',
                accessor: 'age',
                minWidth: 90,
                maxWidth: 90
            }
        ]

        return cols;
    }, [institutions]);

    return columns;
};

const useIneligibleColumns = (institutions: IInstitution[] | null) => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Patient>> = [
            {
                Header: 'Study Number',
                accessor: 'studyNumber',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Initials',
                accessor: 'initials',
                minWidth: 80,
                maxWidth: 80
            },
            {
                id: 'siteCode',
                Header: 'Site Code',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.code;
                },
                Cell: (props: any) => {
                    return (
                        <Chip
                            label={props.cell.value}
                            className={classes.chip}
                            size="small"
                        />
                    );
                },
                minWidth: 80,
                maxWidth: 80
            },
            {
                id: 'institution',
                Header: 'Institution',
                accessor: (patient) => {
                    const institution = institutions?.find(inst => inst.id === patient.institutionId) ?? null;

                    if (!institution) {
                        return null;
                    }

                    return institution?.name;
                },
                minWidth: 200,
                width: 'auto'
            },
            {
                id: 'ineligibleDate',
                Header: 'Date Screen Failed',
                accessor: patient => patient.ineligibleDate,
                Cell: (props: any) => !props.cell.value ? '' : DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy'),
                minWidth: 200,
                width: 'auto'
            },
            {
                id: 'statusReason',
                Header: 'Reason Ineligible',
                accessor: patient => patient.statusReason,
                minWidth: 210,
                width: 'auto'
            }
        ]

        return cols;
    }, [institutions]);

    return columns;
};


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Registration: React.FunctionComponent<IRegistrationProps> = () => {
    const classes = useStyles();

    const params = useParams<IRegistrationParams>();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const treatmentIdParam = queryParams.get("treatmentId");


    const history = useHistory();

    const armCActivationDate = DateTime.fromISO('2025-02-06T00:00:00');


    const [institutions, institutionsLoadState, ] = React.useContext(InstitutionsContext);

    const institutionsToUse = React.useMemo(() => {
        return institutions?.filter(i => (i as Dtos.Institution).activatedDate != null) ?? []
    }, [institutions]);

    const [accrualCount, setAccrualCount] = React.useState<number | null>(null);

    React.useEffect(() => {
        const getAccrualCount = async () => {

            try {
                const response = await client.get(new Dtos.GetAccrualCounts()) as Dtos.GetAccrualCountsResponse;

                if (response.data) {
                    const accrual = Object.keys(response.data).map(key => +response.data[key]).reduce((acc, item) => acc + item, 0);

                    setAccrualCount(accrual);
                }
                else {
                    setAccrualCount(0);
                }
            }
            catch {
                setAccrualCount(0);
            }
        }

        getAccrualCount();
    }, [setAccrualCount]);

    const recruitmentReachedTarget = React.useMemo(() => {
        return (accrualCount ?? 0) >= TARGET_ACCRUAL;
    }, [accrualCount]);

    const institution = React.useMemo(() => {
        return institutions?.find(i => i.code === params.institutionCode) as Dtos.Institution;
    }, [institutions, params.institutionCode]);

    const [[canAdministerOpms], administerPermissionLoadState] = useBctApiPermissions(administerOpmsPermissions);

    const [[canMedicalReview], medicalReviewPermissionLoadState] = useBctApiPermissions(medicalReviewPermissions);

    const [[canUpdatePatient], ] = useBctApiPermissions(patientUpdatePermissions, null, null, params.institutionCode);

    React.useEffect(() => {
        if (!canAdministerOpms &&
            administerPermissionLoadState.state !== RequestState.Pending &&
            administerPermissionLoadState.state !== RequestState.None &&
            !canMedicalReview &&
            medicalReviewPermissionLoadState.state !== RequestState.Pending &&
            medicalReviewPermissionLoadState.state !== RequestState.None &&
            institutions && institutions.length > 0) {

            if (params.institutionCode &&
                params.institutionCode !== ALL_INSTITUTIONS_CODE) {
                history.replace(`/registration/${ALL_COUNTRIES_CODE}/${params.institutionCode}`);
            }
            else {
                history.replace(`/registration/${ALL_COUNTRIES_CODE}/${institutions[0].code}`);
            }
        }
    }, [institutions, canAdministerOpms, administerPermissionLoadState, history, params.institutionCode, canMedicalReview, medicalReviewPermissionLoadState]);

    const institutionsLoading = institutionsLoadState.state === RequestState.None || institutionsLoadState.state === RequestState.Pending;

    const preregisteredColumns = usePreregisteredColumns(institutions);
    //const registeredColumnns = useRegisteredColumns(institutions);
    const randomisationColumns = useRandomisationColumns(institutions);
    const ineligibleColumns = useIneligibleColumns(institutions);

    const getTreatmentId = () => {
        return treatmentIdParam ? +treatmentIdParam : ALL_TREATMENTS_CODE
    }

    const getDate = () => {
        if (treatmentIdParam === '3') {
            return armCActivationDate;
        }
        return undefined;
    }

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs
                    countryName={institutions?.find(i => i.countryCode === params.countryCode)?.country}
                    institutionName={institutions?.find(i => i.code === params.institutionCode)?.name}
                />
            }
            heading="Registration"
        >
            <Paper
                className={classes.container}
            >
                <InstitutionSelector
                    baseRoute="/registration"
                    institutionOnlyRoute="/registration/:countryCode/:institutionCode"
                    countryOnlyRoute="/registration/:countryCode"
                    countryAndInstitutionOnlyRoute="/registration/:countryCode/:institutionCode"
                    countryCode={params.countryCode}
                    institutionCode={params.institutionCode}
                    treatmentId={treatmentIdParam ? +treatmentIdParam : undefined}
                    institutions={institutionsToUse}
                    loading={institutionsLoading}
                    showCountry={canAdministerOpms || canMedicalReview}
                    showTreatment={true}
                    allowAllInstitutions={canAdministerOpms || canMedicalReview}
                    button={
                        <>
                            {
                                params.institutionCode && params.institutionCode !== ALL_INSTITUTIONS_CODE && canUpdatePatient && !recruitmentReachedTarget ?
                                    <Button
                                        className={classes.addPatient}
                                        disabled={institution?.recruitmentDisabled}
                                        variant="contained"
                                        color="primary"
                                        component={Link}
                                        to={`/registration/${params.countryCode ?? ALL_COUNTRIES_CODE}/${params.institutionCode ?? ALL_INSTITUTIONS_CODE}/add-patient`}
                                    >
                                        Add Patient
                                    </Button>
                                    :
                                    <Tooltip
                                        className={classes.addPatient}
                                        title={canUpdatePatient ? (!recruitmentReachedTarget ? 'Select an institution to add a patient' : 'The total number of patients reached the target' ) : 'Insufficient permissions to add a patient'}
                                    >
                                        <div>
                                            <Button
                                                className={classes.addPatient}
                                                disabled={!params.institutionCode || params.institutionCode === ALL_INSTITUTIONS_CODE || !canUpdatePatient || !!recruitmentReachedTarget}
                                                variant="contained"
                                                color="primary"
                                                component={Link}
                                                to={`/registration/${params.countryCode ?? ALL_COUNTRIES_CODE}/${params.institutionCode ?? ALL_INSTITUTIONS_CODE}/add-patient`}
                                            >
                                                Add Patient
                                            </Button>
                                        </div>
                                    </Tooltip>
                            }

                            <Button
                                className={classes.enrolmentStatus}
                                variant="contained"
                                color="primary"
                                component="a"
                                href={`/${params.countryCode ?? ALL_COUNTRIES_CODE}/${params.institutionCode ?? ALL_INSTITUTIONS_CODE}/enrollment-status-report`}
                            >
                                ENROLLMENT STATUS
                            </Button>

                        </>
                    }
                />
                <div
                    className={classes.patientSummaries}
                >
                    {
                        !!institution?.recruitmentDisabled && (
                            <Paper
                                square={true}
                                style={{ background: 'rgba(0, 0, 0, 0.12)', color: '#fff', fontSize: '1rem' }}
                            >
                                <div style={{ background: '#562c85', opacity: '0.38', padding: '12px 16px' }}>Recruitment is disabled for this site</div>
                            </Paper>
                        )
                    }
                    {
                        //!institution?.recruitmentDisabled && (
                        //    <PatientSummaryTable
                        //        title="New"
                        //        patientStatus={Dtos.PatientStatuses.NewPatient}
                        //        countryCode={params.countryCode}
                        //        institutionCode={params.institutionCode}
                        //        columns={preregisteredColumns as Array<Column<IPatient>>}
                        //    />
                        //)
                    }
                    {
                            <PatientSummaryStatusesTable
                                title="Screened"
                                patientStatuses={[Dtos.PatientStatuses.Screened]}
                                countryCode={params.countryCode}
                                treatmentId={getTreatmentId()}
                                institutionCode={params.institutionCode}
                                columns={preregisteredColumns as Array<Column<IPatient>>}
                                dateAfter={getDate()} 
                            />
                    }
                    {
                        //!institution?.recruitmentDisabled && (
                        //    <PatientSummaryTable
                        //        title="Registered"
                        //        patientStatus={Dtos.PatientStatuses.Registered}
                        //        countryCode={params.countryCode}
                        //        institutionCode={params.institutionCode}
                        //        columns={registeredColumnns as Array<Column<IPatient>>}
                        //    />
                        //)
                    }
                    <PatientSummaryStatusesTable
                        title="Registered/Randomised"
                        patientStatuses={[Dtos.PatientStatuses.Registered, Dtos.PatientStatuses.Randomised]}
                        countryCode={params.countryCode}
                        institutionCode={params.institutionCode}
                        treatmentId={getTreatmentId()}
                        columns={randomisationColumns as Array<Column<IPatient>>}

                    />
                    <PatientSummaryStatusesTable
                        title="Ineligible"
                        patientStatuses={[Dtos.PatientStatuses.Ineligible]}
                        countryCode={params.countryCode}
                        institutionCode={params.institutionCode}
                        treatmentId={getTreatmentId()}
                        columns={ineligibleColumns as Array<Column<IPatient>>}
                        dateAfter={getDate()}

                    />
                </div>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default Registration;